/**
 * Hook: Fetch PSE price data (admin only)
 *
 * Calls fetchPSEPriceData GraphQL mutation to trigger backend
 * PSE API fetch, zone classification, and DynamoDB storage.
 */

import { useState } from 'react';
import { getAmplifyClient } from '../../../lib/amplifyClient';

export interface FetchPSEResult {
  success: boolean;
  message?: string;
  importedMonths: string[];
  totalHours: number;
  skippedDates: string[];
  errors?: string[];
}

export interface UseFetchPSEPriceDataReturn {
  fetchPSEData: (startDate: string, endDate: string) => Promise<FetchPSEResult>;
  loading: boolean;
  error: string | null;
  result: FetchPSEResult | null;
  reset: () => void;
}

export function useFetchPSEPriceData(): UseFetchPSEPriceDataReturn {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FetchPSEResult | null>(null);

  const fetchPSEData = async (startDate: string, endDate: string): Promise<FetchPSEResult> => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const client = getAmplifyClient();

      const response = await client.mutations.fetchPSEPriceData({
        input: JSON.stringify({ startDate, endDate }),
      });

      if (response.errors && response.errors.length > 0) {
        throw new Error(response.errors[0].message);
      }

      if (!response.data) {
        throw new Error('No response from server');
      }

      const parsed: FetchPSEResult = typeof response.data === 'string'
        ? JSON.parse(response.data)
        : response.data;

      setResult(parsed);
      return parsed;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch PSE data';
      setError(msg);
      const failResult: FetchPSEResult = {
        success: false,
        message: msg,
        importedMonths: [],
        totalHours: 0,
        skippedDates: [],
        errors: [msg],
      };
      setResult(failResult);
      return failResult;
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setLoading(false);
    setError(null);
    setResult(null);
  };

  return { fetchPSEData, loading, error, result, reset };
}
